import { useEffect, useState, useRef } from 'react';
import api from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import { useTheme } from '../context/ThemeContext.jsx';
import { TIPOS_ID, fechaHora } from '../utils/format.js';
import { PageHeader, Loading } from '../components/UI.jsx';
import { Icon } from '../components/Icons.jsx';

const TABS = [
  { id: 'comercio', label: 'Datos del comercio' },
  { id: 'apariencia', label: 'Apariencia' },
  { id: 'hacienda', label: 'Factura Electronica' },
  { id: 'correo', label: 'Correo de gastos' },
];

// Servidores IMAP mas usados por los clientes; evita que anden buscando el host.
const PRESETS_IMAP = [
  { id: 'hostinger', label: 'Hostinger', host: 'imap.hostinger.com', port: 993 },
  { id: 'gmail', label: 'Gmail / Workspace', host: 'imap.gmail.com', port: 993 },
  { id: 'outlook', label: 'Outlook / Microsoft 365', host: 'outlook.office365.com', port: 993 },
  { id: 'zoho', label: 'Zoho Mail', host: 'imap.zoho.com', port: 993 },
];

// Ancho al que se reduce el logo antes de guardarlo. Da de sobra para el
// encabezado impreso y evita meter un PNG de camara en la base de datos.
const ANCHO_LOGO = 480;

/**
 * Reduce el PNG y lo devuelve como data URI.
 *
 * Se guarda incrustado en la base -no como archivo en disco- para que el
 * mismo logo salga en la pantalla, en el PDF y en el correo, y para que
 * funcione igual en Hostinger y en Render, donde el disco no persiste.
 */
function encogerPng(archivo, anchoMax = ANCHO_LOGO) {
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onerror = () => reject(new Error('No se pudo leer el archivo'));
    lector.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('El archivo no es una imagen valida'));
      img.onload = () => {
        const escala = Math.min(1, anchoMax / img.width);
        const lienzo = document.createElement('canvas');
        lienzo.width = Math.max(1, Math.round(img.width * escala));
        lienzo.height = Math.max(1, Math.round(img.height * escala));
        lienzo.getContext('2d').drawImage(img, 0, 0, lienzo.width, lienzo.height);
        resolve(lienzo.toDataURL('image/png'));
      };
      img.src = lector.result;
    };
    lector.readAsDataURL(archivo);
  });
}

// Bandeja de la que se bajan los XML que mandan los proveedores.
function CorreoGastos() {
  const toast = useToast();
  const [cfg, setCfg] = useState(null);
  const [password, setPassword] = useState('');
  const [probando, setProbando] = useState(false);
  const [revisando, setRevisando] = useState(false);
  const [prueba, setPrueba] = useState(null);
  const [ultimo, setUltimo] = useState(null);

  const cargar = () => api.get('/correo/config').then((r) => setCfg(r.data.data));
  useEffect(() => { cargar(); }, []);

  if (!cfg) return <Loading />;
  const set = (campo, valor) => setCfg({ ...cfg, [campo]: valor });

  const aplicarPreset = (id) => {
    const p = PRESETS_IMAP.find((x) => x.id === id);
    if (p) setCfg({ ...cfg, host: p.host, port: p.port, tls: 1 });
  };

  const guardar = async () => {
    try {
      const { data } = await api.put('/correo/config', {
        ...cfg,
        ...(password ? { password } : {}),
      });
      setCfg(data.data);
      setPassword('');
      toast.success('Correo de gastos guardado');
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo guardar');
    }
  };

  const probar = async () => {
    setProbando(true);
    setPrueba(null);
    try {
      const { data } = await api.post('/correo/probar', {
        host: cfg.host, port: cfg.port, tls: cfg.tls,
        usuario: cfg.usuario, carpeta: cfg.carpeta,
        ...(password ? { password } : {}),
      });
      setPrueba({ ok: true, ...data.data });
    } catch (err) {
      setPrueba({ ok: false, mensaje: err.response?.data?.message || 'No se pudo conectar' });
    } finally {
      setProbando(false);
    }
  };

  const revisar = async () => {
    setRevisando(true);
    setUltimo(null);
    try {
      const { data } = await api.post('/correo/revisar');
      setUltimo(data.data);
      const n = data.data.importados;
      if (n) toast.success(`${n} comprobante(s) importado(s) del correo`);
      else toast.info('No habia comprobantes nuevos');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo leer el correo');
    } finally {
      setRevisando(false);
    }
  };

  return (
    <div className="card fade-up" style={{ padding: 24, maxWidth: 760 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 6, flexWrap: 'wrap', gap: 10 }}>
        <h3>Bandeja de correo de gastos</h3>
        <label className="flex items-center gap-sm" style={{ cursor: 'pointer' }}>
          <span style={{ fontWeight: 600 }}>{cfg.activa ? 'Activada' : 'Desactivada'}</span>
          <input type="checkbox" checked={!!cfg.activa} onChange={(e) => set('activa', e.target.checked ? 1 : 0)}
            style={{ width: 20, height: 20, accentColor: 'var(--accent-vivo)' }} />
        </label>
      </div>
      <p className="muted" style={{ marginBottom: 20 }}>
        Conecte la casilla donde los proveedores mandan sus facturas (por ejemplo
        <strong> facturas@sudominio.com</strong>). El sistema baja los XML adjuntos —tambien
        dentro de ZIP— y los deja en el buzon de Recibidos. Los correos no se borran nunca.
      </p>

      {cfg.falta_migracion && (
        <div style={{ padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13, border: '1px solid var(--danger)' }}>
          Falta correr el ALTER en la base: pegue <code>server/db/ALTER_AL_DIA.sql</code> en
          phpMyAdmin y recargue. Hasta entonces esta pantalla no guarda nada.
        </div>
      )}

      <div className="grid-2" style={{ gap: 14 }}>
        <div className="field">
          <label>Proveedor de correo</label>
          <select className="select" defaultValue="" onChange={(e) => aplicarPreset(e.target.value)}>
            <option value="">Elegir para autocompletar…</option>
            {PRESETS_IMAP.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Servidor IMAP</label>
          <input className="input" value={cfg.host || ''} onChange={(e) => set('host', e.target.value)} placeholder="imap.hostinger.com" />
        </div>
        <div className="field">
          <label>Puerto</label>
          <input className="input" type="number" value={cfg.port || 993} onChange={(e) => set('port', e.target.value)} />
        </div>
        <div className="field">
          <label>Seguridad</label>
          <select className="select" value={cfg.tls ? '1' : '0'} onChange={(e) => set('tls', e.target.value === '1' ? 1 : 0)}>
            <option value="1">SSL / TLS (993)</option>
            <option value="0">Sin cifrar (143)</option>
          </select>
        </div>
        <div className="field">
          <label>Usuario / correo</label>
          <input className="input" value={cfg.usuario || ''} onChange={(e) => set('usuario', e.target.value)} placeholder="facturas@sudominio.com" />
        </div>
        <div className="field">
          <label>
            Contrasena
            {cfg.tiene_password && <span className="badge badge-success" style={{ marginLeft: 6 }}>Guardada</span>}
          </label>
          <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder={cfg.tiene_password ? '•••••• (sin cambios)' : 'Contrasena del correo'} />
        </div>
        <div className="field">
          <label>Carpeta</label>
          <input className="input" value={cfg.carpeta || 'INBOX'} onChange={(e) => set('carpeta', e.target.value)} placeholder="INBOX" />
        </div>
        <div className="field">
          <label>Revisar correos de los ultimos (dias)</label>
          <input className="input" type="number" value={cfg.dias_atras ?? 30} onChange={(e) => set('dias_atras', e.target.value)} />
        </div>
        <div className="field" style={{ gridColumn: '1/-1' }}>
          <label>Solo aceptar de estos remitentes (opcional)</label>
          <input className="input" value={cfg.remitentes_permitidos || ''} onChange={(e) => set('remitentes_permitidos', e.target.value)}
            placeholder="facturacion@proveedor.cr, @otroproveedor.com" />
          <span className="muted" style={{ fontSize: 12 }}>
            Separe con comas. Un valor que empiece con @ acepta todo el dominio. Vacio = cualquier remitente.
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 10, margin: '4px 0 16px' }}>
        <label className="flex items-center gap-sm" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={!!cfg.solo_no_leidos} onChange={(e) => set('solo_no_leidos', e.target.checked ? 1 : 0)}
            style={{ width: 18, height: 18, accentColor: 'var(--accent-vivo)' }} />
          <span>Revisar solo los correos sin leer</span>
        </label>
        <label className="flex items-center gap-sm" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={!!cfg.marcar_leidos} onChange={(e) => set('marcar_leidos', e.target.checked ? 1 : 0)}
            style={{ width: 18, height: 18, accentColor: 'var(--accent-vivo)' }} />
          <span>Marcar como leido despues de importarlo</span>
        </label>
        <label className="flex items-center gap-sm" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={!!cfg.registrar_gasto_auto} onChange={(e) => set('registrar_gasto_auto', e.target.checked ? 1 : 0)}
            style={{ width: 18, height: 18, accentColor: 'var(--accent-vivo)' }} />
          <span>Registrar el gasto automaticamente al importar</span>
        </label>
        {!!cfg.registrar_gasto_auto && (
          <div className="field" style={{ maxWidth: 300, margin: 0 }}>
            <label>Categoria para esos gastos</label>
            <input className="input" value={cfg.categoria_gasto || ''} onChange={(e) => set('categoria_gasto', e.target.value)} />
          </div>
        )}
      </div>

      <div className="flex items-center gap-sm" style={{ padding: 12, borderRadius: 8, background: 'var(--accent-soft)', marginBottom: 16 }}>
        <Icon.alert width={18} height={18} />
        <span style={{ fontSize: 13 }}>
          Con Gmail o Microsoft 365 hay que usar una <strong>contrasena de aplicacion</strong>, no la
          del usuario, y tener IMAP habilitado. El IVA solo se acredita despues de aceptar el
          comprobante en Recibidos, por eso el gasto automatico viene apagado.
        </span>
      </div>

      {prueba && (
        <div style={{
          padding: 12, borderRadius: 8, marginBottom: 14, fontSize: 13,
          border: `1px solid ${prueba.ok ? 'var(--success, #16a34a)' : 'var(--danger)'}`,
        }}>
          {prueba.ok
            ? `Conexion correcta con "${prueba.carpeta}": ${prueba.mensajes} mensaje(s), ${prueba.no_leidos} sin leer.`
            : prueba.mensaje}
        </div>
      )}

      {ultimo && (
        <div style={{ padding: 12, borderRadius: 8, marginBottom: 14, fontSize: 13, border: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {ultimo.correos_revisados} correo(s) revisado(s) · {ultimo.importados} importado(s) ·{' '}
            {ultimo.duplicados} repetido(s) · {ultimo.fallidos} con error
          </div>
          {ultimo.detalles.slice(0, 12).map((d, i) => (
            <div key={i} className="muted" style={{ display: 'flex', gap: 8 }}>
              <span style={{ color: d.ok ? 'var(--success, #16a34a)' : 'var(--text-muted)' }}>{d.ok ? '✓' : '·'}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.archivo} — {d.ok ? d.emisor : d.motivo}
              </span>
            </div>
          ))}
        </div>
      )}

      {cfg.ultima_revision && (
        <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
          Ultima revision: {fechaHora(cfg.ultima_revision)}
          {cfg.ultimo_resultado ? ` — ${cfg.ultimo_resultado}` : ''}
        </p>
      )}

      <div className="flex gap-sm" style={{ flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={guardar}><Icon.check /> Guardar configuracion</button>
        <button className="btn btn-outline" onClick={probar} disabled={probando}>
          {probando ? 'Probando…' : 'Probar conexion'}
        </button>
        <button className="btn btn-outline" onClick={revisar} disabled={revisando || cfg.sin_configurar}>
          {revisando ? 'Leyendo correo…' : 'Revisar bandeja ahora'}
        </button>
      </div>
    </div>
  );
}

// Revision previa: Hacienda rechaza el comprobante entero por un campo mal
// puesto y avisa en diferido. Mejor ver la lista antes de pasar a produccion.
function RevisionFiscal() {
  const [rev, setRev] = useState(null);
  const [abierto, setAbierto] = useState(false);

  const revisar = () => {
    setRev('cargando');
    api.get('/empresa/hacienda/revision')
      .then((r) => { setRev(r.data.data); setAbierto(true); })
      .catch(() => setRev(null));
  };

  const color = rev && rev !== 'cargando'
    ? (rev.listo_para_produccion ? 'var(--success, #16a34a)' : 'var(--danger)')
    : 'var(--border)';

  return (
    <div style={{ padding: 14, borderRadius: 'var(--radius-sm)', border: `1px solid ${color}`, marginBottom: 18 }}>
      <div className="flex items-center justify-between" style={{ gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 600 }}>Revision antes de facturar</div>
          <div className="muted" style={{ fontSize: 13 }}>
            Verifica cedula, ubicacion en codigos, actividad, llave y codigos CAByS.
          </div>
        </div>
        <button className="btn btn-outline btn-sm" onClick={revisar} disabled={rev === 'cargando'}>
          {rev === 'cargando' ? 'Revisando...' : 'Revisar ahora'}
        </button>
      </div>

      {abierto && rev && rev !== 'cargando' && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 700, color, marginBottom: 8 }}>
            {rev.listo_para_produccion
              ? 'Todo listo para facturar en produccion'
              : `${rev.graves} problema(s) que impiden facturar`}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rev.hallazgos.map((h, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                <span style={{ color: h.grave ? 'var(--danger)' : '#d97706', fontWeight: 700 }}>
                  {h.grave ? '✗' : '!'}
                </span>
                <div>
                  <div style={{ fontWeight: 600 }}>{h.campo}</div>
                  <div>{h.mensaje}</div>
                  <div className="muted">{h.como_resolver}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Configuracion() {
  const toast = useToast();
  const { theme, setTheme } = useTheme();
  const [tab, setTab] = useState('comercio');
  const [empresa, setEmpresa] = useState(null);
  const [hac, setHac] = useState(null);
  const fileRef = useRef();

  // credenciales nuevas (no se precargan por seguridad)
  const [cred, setCred] = useState({ usuario_api: '', password_api: '', pin_llave: '' });
  const [subiendo, setSubiendo] = useState(false);
  const logoRef = useRef();
  const [cargandoLogo, setCargandoLogo] = useState(false);

  useEffect(() => {
    api.get('/empresa').then((r) => setEmpresa(r.data.data || {}));
    api.get('/empresa/hacienda').then((r) => {
      setHac(r.data.data || {});
      if (r.data.data) setCred((c) => ({ ...c, usuario_api: r.data.data.usuario_api || '' }));
    }).catch(() => setHac({}));
  }, []);

  const guardarEmpresa = async () => {
    try { await api.put('/empresa', empresa); toast.success('Datos del comercio guardados'); }
    catch (err) { toast.error('Error al guardar'); }
  };

  const guardarHacienda = async () => {
    try {
      await api.put('/empresa/hacienda', {
        activa: hac.activa, ambiente: hac.ambiente,
        sucursal: hac.sucursal, terminal: hac.terminal,
        usuario_api: cred.usuario_api,
        ...(cred.password_api ? { password_api: cred.password_api } : {}),
        ...(cred.pin_llave ? { pin_llave: cred.pin_llave } : {}),
      });
      toast.success('Configuracion de Hacienda guardada');
      setCred((c) => ({ ...c, password_api: '', pin_llave: '' }));
      api.get('/empresa/hacienda').then((r) => setHac(r.data.data || {}));
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const subirLlave = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('llave', file);
      const { data } = await api.post('/empresa/hacienda/llave', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Llave .p12 guardada de forma cifrada');
      api.get('/empresa/hacienda').then((r) => setHac(r.data.data || {}));
    } catch (err) { toast.error('No se pudo subir la llave'); }
    finally { setSubiendo(false); }
  };

  if (!empresa || !hac) return <Loading />;
  const set = (campo, valor) => setEmpresa({ ...empresa, [campo]: valor });

  const elegirLogo = async (archivo) => {
    if (!archivo) return;
    if (archivo.type !== 'image/png') {
      toast.error('El logo tiene que ser un archivo PNG');
      return;
    }
    if (archivo.size > 5 * 1024 * 1024) {
      toast.error('El PNG no puede pesar mas de 5 MB');
      return;
    }
    setCargandoLogo(true);
    try {
      set('logo_url', await encogerPng(archivo));
      toast.success('Logo listo. Guarde los cambios para aplicarlo.');
    } catch (err) {
      toast.error(err.message || 'No se pudo procesar la imagen');
    } finally {
      setCargandoLogo(false);
      if (logoRef.current) logoRef.current.value = '';
    }
  };

  return (
    <div>
      <PageHeader title="Configuracion" subtitle="Personaliza tu sistema. Nada esta fijo en codigo." />

      <div className="flex gap-sm" style={{ marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button key={t.id} className={`btn ${tab === t.id ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* ---- Datos del comercio ---- */}
      {tab === 'comercio' && (
        <div className="card fade-up" style={{ padding: 24, maxWidth: 760 }}>
          <h3 style={{ marginBottom: 4 }}>Logo del comprobante</h3>
          <p className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
            Sale arriba del nombre del negocio en la factura, el PDF y el correo. Archivo PNG.
          </p>
          <div className="flex items-center gap-sm" style={{ flexWrap: 'wrap', marginBottom: 24 }}>
            <div style={{
              width: 132, height: 76, display: 'grid', placeItems: 'center', flexShrink: 0,
              border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)',
              background: 'var(--surface)', overflow: 'hidden', padding: 6,
            }}>
              {empresa.logo_url ? (
                <img src={empresa.logo_url} alt="Logo del negocio"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              ) : (
                <span className="muted" style={{ fontSize: 11 }}>Sin logo</span>
              )}
            </div>
            <input ref={logoRef} type="file" accept="image/png" style={{ display: 'none' }}
              onChange={(e) => elegirLogo(e.target.files?.[0])} />
            <button className="btn btn-outline" disabled={cargandoLogo}
              onClick={() => logoRef.current?.click()}>
              {cargandoLogo ? 'Procesando...' : empresa.logo_url ? 'Cambiar PNG' : 'Subir PNG'}
            </button>
            {empresa.logo_url && (
              <button className="btn btn-outline" onClick={() => set('logo_url', '')}>Quitar</button>
            )}
          </div>

          <h3 style={{ marginBottom: 18 }}>Informacion del negocio</h3>
          <div className="grid-2" style={{ gap: 14 }}>
            <div className="field"><label>Nombre comercial</label><input className="input" value={empresa.nombre_comercial || ''} onChange={(e) => set('nombre_comercial', e.target.value)} /></div>
            <div className="field"><label>Razon social</label><input className="input" value={empresa.razon_social || ''} onChange={(e) => set('razon_social', e.target.value)} /></div>
            <div className="field">
              <label>Tipo de identificacion</label>
              <select className="select" value={empresa.tipo_identificacion || '02'} onChange={(e) => set('tipo_identificacion', e.target.value)}>
                {TIPOS_ID.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="field"><label>Cedula / identificacion</label><input className="input" value={empresa.identificacion || ''} onChange={(e) => set('identificacion', e.target.value)} /></div>
            <div className="field"><label>Telefono</label><input className="input" value={empresa.telefono || ''} onChange={(e) => set('telefono', e.target.value)} /></div>
            <div className="field"><label>Correo</label><input className="input" type="email" value={empresa.email || ''} onChange={(e) => set('email', e.target.value)} /></div>
            <div className="field"><label>Provincia</label><input className="input" value={empresa.provincia || ''} onChange={(e) => set('provincia', e.target.value)} /></div>
            <div className="field"><label>Canton</label><input className="input" value={empresa.canton || ''} onChange={(e) => set('canton', e.target.value)} /></div>
            <div className="field"><label>Distrito</label><input className="input" value={empresa.distrito || ''} onChange={(e) => set('distrito', e.target.value)} /></div>
            <div className="field"><label>Barrio</label><input className="input" value={empresa.barrio || ''} onChange={(e) => set('barrio', e.target.value)} /></div>
            <div className="field" style={{ gridColumn: '1/-1' }}><label>Direccion exacta</label><input className="input" value={empresa.direccion_exacta || ''} onChange={(e) => set('direccion_exacta', e.target.value)} /></div>
            <div className="field"><label>Codigo de actividad economica</label><input className="input" value={empresa.codigo_actividad || ''} onChange={(e) => set('codigo_actividad', e.target.value)} /></div>
            <div className="field"><label>Moneda</label><input className="input" value={empresa.moneda || 'CRC'} disabled /></div>
            <div className="field" style={{ gridColumn: '1/-1' }}><label>Mensaje al pie de la factura</label><input className="input" value={empresa.mensaje_factura || ''} onChange={(e) => set('mensaje_factura', e.target.value)} /></div>
          </div>

          <label className="flex items-center gap-sm" style={{ cursor: 'pointer', marginTop: 6 }}>
            <input type="checkbox" checked={empresa.enviar_correo_auto == null ? true : !!empresa.enviar_correo_auto}
              onChange={(e) => set('enviar_correo_auto', e.target.checked ? 1 : 0)}
              style={{ width: 18, height: 18, accentColor: 'var(--accent-vivo)' }} />
            <span>
              Mandar el comprobante al cliente apenas se cobra
              <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                Solo si el cliente tiene correo. Va el PDF adjunto con consecutivo y clave numerica.
              </span>
            </span>
          </label>
          <div style={{ marginTop: 12 }}><button className="btn btn-primary" onClick={guardarEmpresa}><Icon.check /> Guardar cambios</button></div>
        </div>
      )}

      {/* ---- Apariencia ---- */}
      {tab === 'apariencia' && (
        <div className="card fade-up" style={{ padding: 24, maxWidth: 560 }}>
          <h3 style={{ marginBottom: 6 }}>Tema de la aplicacion</h3>
          <p className="muted" style={{ marginBottom: 18 }}>Elige como se ve el sistema. Se guarda en este dispositivo.</p>
          <div className="grid-2" style={{ gap: 14 }}>
            {['light', 'dark'].map((t) => (
              <button key={t} onClick={() => setTheme(t)}
                className="card" style={{ padding: 18, cursor: 'pointer', textAlign: 'left', border: theme === t ? '2px solid var(--accent-vivo)' : '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  {t === 'light' ? <Icon.sun /> : <Icon.moon />}
                  <strong>{t === 'light' ? 'Claro' : 'Oscuro'}</strong>
                  {theme === t && <span className="badge badge-accent" style={{ marginLeft: 'auto' }}>Activo</span>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <span style={{ flex: 1, height: 30, borderRadius: 6, background: t === 'light' ? '#f8f5ff' : '#0c0616', border: '1px solid var(--border)' }} />
                  <span style={{ width: 30, height: 30, borderRadius: 6, background: t === 'light' ? '#5b21b6' : '#6b21a8' }} />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ---- Factura electronica ---- */}
      {tab === 'hacienda' && (
        <div className="card fade-up" style={{ padding: 24, maxWidth: 720 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 6, flexWrap: 'wrap', gap: 10 }}>
            <h3>Factura Electronica (Hacienda v4.4)</h3>
            <label className="flex items-center gap-sm" style={{ cursor: 'pointer' }}>
              <span style={{ fontWeight: 600 }}>{hac.activa ? 'Activada' : 'Desactivada'}</span>
              <input type="checkbox" checked={!!hac.activa} onChange={(e) => setHac({ ...hac, activa: e.target.checked })} style={{ width: 20, height: 20, accentColor: 'var(--accent-vivo)' }} />
            </label>
          </div>
          <p className="muted" style={{ marginBottom: 20 }}>
            Cada negocio sube aqui su propia llave y credenciales. Todo se guarda cifrado. Mientras este desactivada, las ventas se registran como comprobantes internos.
          </p>

          <RevisionFiscal />


          {/* Ambiente */}
          <div className="grid-2" style={{ gap: 14, marginBottom: 6 }}>
            <div className="field">
              <label>Ambiente</label>
              <select className="select" value={hac.ambiente || 'sandbox'} onChange={(e) => setHac({ ...hac, ambiente: e.target.value })}>
                <option value="simulacion">Simulacion local (sin Hacienda)</option>
                <option value="sandbox">Pruebas (Sandbox de Hacienda)</option>
                <option value="prod">Produccion</option>
              </select>
            </div>
            <div className="field"><label>Usuario de API (Hacienda)</label><input className="input" value={cred.usuario_api} onChange={(e) => setCred({ ...cred, usuario_api: e.target.value })} placeholder="cpf-01-..." /></div>
            <div className="field"><label>Contrasena de API {hac.tiene_password && <span className="badge badge-success" style={{ marginLeft: 6 }}>Guardada</span>}</label><input className="input" type="password" value={cred.password_api} onChange={(e) => setCred({ ...cred, password_api: e.target.value })} placeholder={hac.tiene_password ? '•••••• (sin cambios)' : 'Contrasena'} /></div>
            <div className="field"><label>Sucursal</label><input className="input" value={hac.sucursal || '001'} onChange={(e) => setHac({ ...hac, sucursal: e.target.value })} /></div>
            <div className="field"><label>Terminal</label><input className="input" value={hac.terminal || '00001'} onChange={(e) => setHac({ ...hac, terminal: e.target.value })} /></div>
          </div>

          {/* Llave .p12 */}
          <div style={{ padding: 16, borderRadius: 'var(--radius-sm)', background: 'var(--accent-soft)', marginBottom: 16 }}>
            <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 10 }}>
              <div className="flex items-center gap-sm">
                <Icon.key />
                <div>
                  <div style={{ fontWeight: 600 }}>Llave criptografica (.p12)</div>
                  <div className="muted" style={{ fontSize: 13 }}>
                    {hac.tiene_llave ? `Cargada: ${hac.llave_nombre || 'llave.p12'}` : 'Aun no has subido tu llave'}
                  </div>
                </div>
              </div>
              <input ref={fileRef} type="file" accept=".p12" style={{ display: 'none' }} onChange={subirLlave} />
              <button className="btn btn-ghost" onClick={() => fileRef.current?.click()} disabled={subiendo}>
                {subiendo ? <span className="spinner" /> : <><Icon.key width={16} height={16} /> {hac.tiene_llave ? 'Reemplazar' : 'Subir'} llave</>}
              </button>
            </div>
            <div className="field" style={{ marginTop: 12, marginBottom: 0 }}>
              <label>PIN de la llave {hac.tiene_pin && <span className="badge badge-success" style={{ marginLeft: 6 }}>Guardado</span>}</label>
              <input className="input" type="password" value={cred.pin_llave} onChange={(e) => setCred({ ...cred, pin_llave: e.target.value })} placeholder={hac.tiene_pin ? '•••• (sin cambios)' : 'PIN del certificado'} />
            </div>
          </div>

          <div className="flex items-center gap-sm" style={{ padding: 12, borderRadius: 8, background: 'var(--accent-soft)', marginBottom: 16 }}>
            <Icon.alert width={18} height={18} />
            <span style={{ fontSize: 13 }}>
              El servidor firma el XML (XAdES-EPES) y lo envia a Recepcion. Use <strong>Simulacion local</strong> + <code>npm run simulador</code> y una llave de prueba (<code>npm run llave:prueba</code>) para validar el circuito sin ATV. Sandbox y produccion requieren usuario/clave de API y certificado MICITT.
            </span>
          </div>

          <button className="btn btn-primary" onClick={guardarHacienda}><Icon.check /> Guardar configuracion</button>
        </div>
      )}

      {/* ---- Correo de gastos ---- */}
      {tab === 'correo' && <CorreoGastos />}
    </div>
  );
}
